import { useState } from 'react'
import { ArrowLeftIcon, SendIcon } from 'lucide-react'
import { Box, Stack, TableBody, TableHead, TableRow, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'

import { Button, Card, Skeleton } from '@repo/ui'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { ConfirmSettingModal } from 'components/feedback/modals/ConfirmSettingModal/ConfirmSettingModal'
import { ActionDropdown } from 'components/input/ActionDropdown/ActionDropdown'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import { useBuildSeoReview, useSeoReview, useSeoReviews } from 'hooks/api'
import type { SeoReview, SeoReviewMove, SeoReviewNotified, SeoReviewsResponse } from 'services'

import {
  Cell,
  EmptyRow,
  HeadCell,
  Notice,
  SectionNote,
  StatTable,
  ToneChip,
  dateTime,
  type Tone,
} from '../shared'
import { fmt, fmtUsd } from './seoModel'

const ADMIN_ONLY = 'Only platform admins can build or send a review.'

const place = (p: number | null) => (p === null ? 'not ranked' : `#${p}`)

const notifiedChip = (n: SeoReviewNotified | null, channel: 'email' | 'slack') => {
  if (!n) return <ToneChip tone="neutral" label="Not sent" />
  const s = n[channel].status
  const tone: Tone = s === 'sent' ? 'positive' : s === 'partial' || s === 'failed' ? 'negative' : 'neutral'
  const label =
    s === 'sent'
      ? channel === 'email'
        ? `Sent to ${n.email.sent}`
        : 'Posted'
      : s === 'partial'
        ? `${n.email.sent} of ${n.email.recipients}`
        : s === 'failed'
          ? 'Failed'
          : s === 'not_configured'
            ? 'Not set up'
            : 'Not sent'
  return <ToneChip tone={tone} label={label} />
}

const MovesTable = ({ title, rows }: { title: string; rows: SeoReviewMove[] }) => (
  <StatTable>
    <TableHead>
      <TableRow>
        <HeadCell>{title}</HeadCell>
        <HeadCell>Market</HeadCell>
        <HeadCell align="right">Before</HeadCell>
        <HeadCell align="right">Now</HeadCell>
        <HeadCell align="right">Searches / month</HeadCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.length === 0 ? (
        <EmptyRow colSpan={5} text="None this cycle." />
      ) : (
        rows.map((m) => (
          <TableRow key={`${m.market}:${m.keyword}`}>
            <Cell>{m.keyword}</Cell>
            <Cell>{m.market.toUpperCase()}</Cell>
            <Cell align="right">{place(m.from)}</Cell>
            <Cell align="right" strong>
              {place(m.to)}
            </Cell>
            <Cell align="right">{fmt(m.volume)}</Cell>
          </TableRow>
        ))
      )}
    </TableBody>
  </StatTable>
)

const Figure = ({ label, value, note }: { label: string; value: string; note?: string }) => (
  <Stack spacing={0.5} sx={{ minWidth: 160, flex: 1 }}>
    <Typography variant="body3" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
    {note ? (
      <Typography variant="body3" color="text.secondary">
        {note}
      </Typography>
    ) : null}
  </Stack>
)

const ReviewDetail = ({ review, onBack }: { review: SeoReview; onBack: () => void }) => {
  const p = review.payload
  return (
    <Card
      title={`Review, ${dateTime(review.createdAt)}`}
      action={
        <Button variant="text" onClick={onBack} startIcon={<ArrowLeftIcon size={16} strokeWidth={1.5} />}>
          All reviews
        </Button>
      }
    >
      <Stack spacing={5} sx={{ mt: 4 }}>
        <SectionNote>
          {p.manual ? 'Built by hand. ' : ''}
          {p.cycles.length
            ? `After the rankings cycles ${p.cycles
                .map((c) => `${c.market.toUpperCase()} ${dateTime(c.claimedAt)} (${c.done} checked, ${c.expired} expired)`)
                .join(', ')}.`
            : 'No rankings cycle yet.'}
        </SectionNote>
        {review.narrative ? (
          <Box sx={{ borderLeft: (t) => `3px solid ${t.palette.brand[500]}`, pl: 3 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
              {review.narrative}
            </Typography>
            <Typography variant="body3" color="text.secondary">
              Summary written by {review.narrativeModel ?? 'Gemini'}
              {review.narrativeCostUsd !== null ? ` (${fmtUsd(review.narrativeCostUsd)})` : ''}. The
              figures below are the record.
            </Typography>
          </Box>
        ) : (
          <SectionNote>No written summary: {review.narrativeSkippedReason ?? 'skipped.'}</SectionNote>
        )}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ flexWrap: 'wrap', rowGap: 4 }}>
          {p.gsc ? (
            <>
              <Figure
                label={`Search Console clicks, ${p.gsc.since} to ${p.gsc.until}`}
                value={fmt(p.gsc.clicks)}
                note={`was ${fmt(p.gsc.previous.clicks)}`}
              />
              <Figure
                label="Impressions"
                value={fmt(p.gsc.impressions)}
                note={`was ${fmt(p.gsc.previous.impressions)}`}
              />
            </>
          ) : null}
          <Figure
            label="Tracked terms improved / declined"
            value={`${p.moved.improved.length + p.moved.entered.length} / ${p.moved.declined.length + p.moved.dropped.length}`}
            note={`of ${fmt(p.moved.compared)} compared`}
          />
          <Figure label="Open recommendations" value={fmt(p.openCount)} />
          <Figure
            label={`Spend, ${p.spend.month}`}
            value={`${fmt(p.spend.credits)} credits`}
            note={`of ${fmt(p.spend.creditCap)}; LLM ${fmtUsd(p.spend.costUsd)} of ${fmtUsd(p.spend.costCapUsd)}`}
          />
        </Stack>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Top recommendations at the time
          </Typography>
          {p.top.length === 0 ? (
            <SectionNote>None open.</SectionNote>
          ) : (
            p.top.map((r, i) => (
              <Stack key={r.id} spacing={0.5}>
                <Typography variant="body2">
                  {i + 1}. {r.title} <Typography component="span" variant="body3" color="text.secondary">({r.ruleLabel})</Typography>
                </Typography>
                <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  {r.suggestedAction}
                </Typography>
              </Stack>
            ))
          )}
        </Stack>
        <MovesTable title="Wins" rows={p.wins} />
        <MovesTable title="Losses" rows={p.losses} />
        {p.competitorOvertakes.length ? (
          <SectionNote>
            Competitors that moved above Vera:{' '}
            {p.competitorOvertakes.map((o) => `${o.domain} on "${o.keyword}" (${o.market.toUpperCase()})`).join('; ')}.
          </SectionNote>
        ) : null}
        <Stack direction="row" spacing={3} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 2 }}>
          <Typography variant="body3" color="text.secondary">
            Email
          </Typography>
          {notifiedChip(review.notified, 'email')}
          <Typography variant="body3" color="text.secondary">
            Slack
          </Typography>
          {notifiedChip(review.notified, 'slack')}
          {review.notified?.email.reason ? (
            <Typography variant="body3" color="text.secondary">
              {review.notified.email.reason}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Card>
  )
}

const Readiness = ({ data }: { data: SeoReviewsResponse }) => {
  const r = data.readiness
  return (
    <SectionNote>
      {r.ready
        ? `The US and UK cycles are complete; the next scheduled tick builds and sends this cycle's review if it has not already.`
        : `Next review: waiting (${r.reason ?? 'no cycle yet'}).`}{' '}
      Sends to {data.channels.emailRecipients} email recipient{data.channels.emailRecipients === 1 ? '' : 's'}
      {data.channels.slack ? ' and Slack' : ' (Slack not set up)'}
      {data.channels.narrative ? ', with a short Gemini summary when Vertex is configured' : ''}.
    </SectionNote>
  )
}

/**
 * VRG-643 follow-up — Review history: every biweekly review, newest first, and one
 * review's full record. Built after each completed US + UK rankings cycle by the
 * scheduled job (which also emails it), or by hand here.
 */
export const SeoReviewsCard = () => {
  const [params, setParams] = useSearchParams()
  const selected = params.get('review')
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const { data, isLoading, error } = useSeoReviews()
  const detail = useSeoReview(selected)
  const build = useBuildSeoReview()
  const { open: notify } = useSnackbar()
  const [confirmSend, setConfirmSend] = useState(false)

  const select = (id: string | null) => {
    const next = new URLSearchParams(params)
    if (id) next.set('review', id)
    else next.delete('review')
    setParams(next, { replace: false })
  }

  const run = (send: boolean) =>
    build.mutate(
      { notify: send },
      {
        onSuccess: (r) => {
          setConfirmSend(false)
          const id = (r.detail?.reviewId as string | undefined) ?? null
          notify({
            type: r.status === 'done' ? 'success' : 'error',
            text:
              r.status === 'done'
                ? send
                  ? `Review built and sent (email ${String(r.detail?.email)}, Slack ${String(r.detail?.slack)}).`
                  : 'Review built. Nothing was sent.'
                : `Review not built: ${r.error ?? r.status}`,
          })
          if (id) select(id)
        },
        onError: (e) => {
          setConfirmSend(false)
          notify({ type: 'error', text: (e as Error).message })
        },
      },
    )

  if (selected) {
    if (detail.isLoading)
      return (
        <Card>
          <Skeleton height={14} lines={10} />
        </Card>
      )
    if (detail.error || !detail.data)
      return (
        <Card title="Review">
          <Stack spacing={3} sx={{ mt: 4 }}>
            <SectionNote>Could not load this review: {(detail.error as Error | null)?.message ?? 'not found'}.</SectionNote>
            <Box>
              <Button variant="secondary" onClick={() => select(null)}>
                All reviews
              </Button>
            </Box>
          </Stack>
        </Card>
      )
    return <ReviewDetail review={detail.data} onBack={() => select(null)} />
  }

  return (
    <Card
      title="Review history"
      action={
        <Stack direction="row" spacing={3}>
          <ActionDropdown
            variant="button"
            items={[
              {
                label: `Build and send to ${data?.channels.emailRecipients ?? 0} recipient${
                  data?.channels.emailRecipients === 1 ? '' : 's'
                }${data?.channels.slack ? ' and Slack' : ''}`,
                icon: <SendIcon size={18} strokeWidth={1} />,
                disabled: !isAdmin || build.isPending,
                tooltip: !isAdmin ? ADMIN_ONLY : undefined,
                onClick: () => setConfirmSend(true),
              },
            ]}
          />
          <Button
            variant="primary"
            disabled={!isAdmin || build.isPending}
            disabledReason={!isAdmin ? ADMIN_ONLY : undefined}
            onClick={() => run(false)}
          >
            Build review now
          </Button>
        </Stack>
      }
    >
      <Stack spacing={4} sx={{ mt: 4 }}>
        {isLoading ? (
          <Skeleton height={14} lines={6} />
        ) : error || !data ? (
          <SectionNote>Could not load reviews: {(error as Error | null)?.message}</SectionNote>
        ) : (
          <>
            <Readiness data={data} />
            {!data.channels.emailRecipients ? (
              <Notice severity="warning">SEO_REVIEW_RECIPIENTS holds no valid address, so reviews are stored but not emailed.</Notice>
            ) : null}
            <StatTable>
              <TableHead>
                <TableRow>
                  <HeadCell>Built</HeadCell>
                  <HeadCell>After cycles</HeadCell>
                  <HeadCell align="right">Improved / declined</HeadCell>
                  <HeadCell align="right">Open</HeadCell>
                  <HeadCell>
                    Summary
                    <InfoTooltip term="seo-review" />
                  </HeadCell>
                  <HeadCell>Email</HeadCell>
                  <HeadCell>Slack</HeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.reviews.length === 0 ? (
                  <EmptyRow colSpan={7} text="No reviews yet. The first one is built after the first complete US and UK rankings cycle." />
                ) : (
                  data.reviews.map((r) => (
                    <TableRow
                      key={r.id}
                      hover
                      onClick={() => select(r.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <Cell strong>{dateTime(r.createdAt)}</Cell>
                      <Cell>
                        {r.summary.manual && !r.summary.cycles.length
                          ? 'Built by hand'
                          : r.summary.cycles.map((c) => `${c.market.toUpperCase()} ${dateTime(c.claimedAt)}`).join(', ')}
                      </Cell>
                      <Cell align="right">
                        {fmt(r.summary.improved)} / {fmt(r.summary.declined)}
                      </Cell>
                      <Cell align="right">{fmt(r.summary.open)}</Cell>
                      <Cell>{r.narrative ? 'Written' : 'None'}</Cell>
                      <Cell>{notifiedChip(r.notified, 'email')}</Cell>
                      <Cell>{notifiedChip(r.notified, 'slack')}</Cell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </StatTable>
          </>
        )}
      </Stack>
      <ConfirmSettingModal
        open={confirmSend}
        onClose={() => setConfirmSend(false)}
        onConfirm={() => run(true)}
        loading={build.isPending}
        title="Build and send a review"
        consequence={`This recomputes the recommendations, builds a review now and emails it to ${
          data?.channels.emailRecipients ?? 0
        } recipient${data?.channels.emailRecipients === 1 ? '' : 's'}${
          data?.channels.slack ? ', and posts it to Slack' : ''
        }. The scheduled review after the next rankings cycle still goes out as usual.`}
        confirmLabel="Build and send"
      />
    </Card>
  )
}
